<template>
  <div class="min-h-screen bg-gray-50">
    <Header />
    
    <main class="p-6">
      <div class="max-w-7xl mx-auto">
        <!-- Header Section -->
        <div class="flex items-center justify-between mb-6">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">Links</h1>
            <p class="text-gray-600 mt-2">Manage your short links</p>
          </div>
          <button
            @click="showCreateModal = true"
            class="btn btn-primary"
          >
            Create New Link
          </button>
        </div>

        <!-- Error Message -->
        <ErrorMessage 
          v-if="linksStore.error" 
          :message="linksStore.error" 
          title="Error Loading Links"
        />

        <!-- Loading State -->
        <div v-if="linksStore.isLoading" class="text-center py-8">
          <div class="spinner mx-auto"></div>
          <p class="text-gray-600 mt-2">Loading links...</p>
        </div>

        <!-- Links Table -->
        <div v-else-if="linksStore.links.length > 0" class="card">
          <div class="card-header">
            <div class="flex items-center justify-between">
              <h2 class="text-xl font-semibold text-gray-900">
                All Links ({{ linksStore.links.length }})
              </h2>
              <div class="flex items-center gap-4">
                <!-- Tenant Filter -->
                <select v-model="selectedTenant" class="form-input">
                  <option value="">All Tenants</option>
                  <option v-for="t in adminStore.tenants" :key="t.tenantId" :value="t.tenantId">
                    {{ t.name }}
                  </option>
                </select>
                <!-- Search -->
                <div class="relative flex-1 max-w-md">
                  <input
                    v-model="searchTerm"
                    type="text"
                    placeholder="Search links..."
                    class="form-input pl-10 pr-4 w-full"
                  />
                  <svg 
                    class="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <!-- Sort -->
                <select v-model="sortBy" class="form-input">
                  <option value="createdAt">Created Date</option>
                  <option value="clickCount">Click Count</option>
                  <option value="title">Title</option>
                </select>
              </div>
            </div>
          </div>
          
          <div class="card-body p-0">
            <table class="table">
              <thead>
                <tr>
                  <th>Short Code</th>
                  <th>Title</th>
                  <th>Redirect URL</th>
                  <th>Clicks</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="link in filteredAndSortedLinks"
                  :key="link.shortCode"
                  class="hover:bg-gray-50 cursor-pointer"
                  @click="openLinkDetail(link)"
                >
                  <td>
                    <div class="flex items-center gap-2">
                      <span class="shortcode-outline">
                        {{ link.shortCode }}
                      </span>
                      <button
                        @click.stop="copyShortUrl(link.shortCode)"
                        class="text-gray-400 hover:text-gray-600"
                        title="Copy short URL"
                      >
                        📋
                      </button>
                    </div>
                  </td>
                  <td>
                    <span class="font-medium">{{ link.title || 'Untitled' }}</span>
                  </td>
                  <td>
                    <div class="max-w-xs truncate" :title="link.redirectURL">
                      {{ link.redirectURL }}
                    </div>
                  </td>
                  <td>
                    <span class="font-semibold">{{ link.clickCount }}</span>
                  </td>
                  <td>
                    <div class="flex flex-col gap-1">
                      <span
                        :class="link.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                        class="px-2 py-1 text-xs rounded-full"
                      >
                        {{ link.isActive ? 'Active' : 'Inactive' }}
                      </span>
                      <span 
                        v-if="link.expiresAt && link.expiresAt < Date.now()" 
                        class="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800"
                      >
                        Expired
                      </span>
                    </div>
                  </td>
                  <td>
                    <span class="text-sm text-gray-600">
                      {{ formatDate(link.createdAt) }}
                    </span>
                  </td>
                  <td>
                    <div class="flex items-center gap-2">
                      <button
                        @click.stop="editLink(link)"
                        class="btn btn-secondary btn-sm"
                      >
                        Edit
                      </button>
                      <button
                        @click.stop="deleteLink(link.shortCode)"
                        class="btn btn-danger btn-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-12">
          <div class="text-gray-400 text-6xl mb-4">🔗</div>
          <h3 class="text-xl font-semibold text-gray-900 mb-2">No links yet</h3>
          <p class="text-gray-600 mb-6">Create your first short link to get started</p>
          <button
            @click="showCreateModal = true"
            class="btn btn-primary"
          >
            Create Your First Link
          </button>
        </div>
      </div>
    </main>

    <!-- Create/Edit Link Modal -->
    <LinkFormModal
      v-if="showCreateModal || editingLink"
      :link="editingLink"
      @close="closeModal"
      @save="handleSaveLink"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useLinksStore } from '@/stores/links'
import Header from '@/components/common/Header.vue'
import ErrorMessage from '@/components/common/ErrorMessage.vue'
import LinkFormModal from '@/components/links/LinkFormModal.vue'
import type { Link } from '@/types'
import { useAdminStore } from '@/stores/admin'

const linksStore = useLinksStore()
const adminStore = useAdminStore()
const router = useRouter()

// State
const showCreateModal = ref(false)
const editingLink = ref<Link | null>(null)
const searchTerm = ref('')
const sortBy = ref<'createdAt' | 'clickCount' | 'title'>('createdAt')
const selectedTenant = ref<string>('')

// Computed
const filteredAndSortedLinks = computed(() => {
  let filtered = linksStore.links
  // Filter by tenant if selected (client-side filter as fallback)
  if (selectedTenant.value) {
    filtered = filtered.filter(l => l.tenantId === selectedTenant.value)
  }

  // Filter by search term
  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase()
    filtered = filtered.filter(link => 
      link.shortCode.toLowerCase().includes(term) ||
      (link.title && link.title.toLowerCase().includes(term)) ||
      link.redirectURL.toLowerCase().includes(term)
    )
  }

  // Sort
  return filtered.sort((a, b) => {
    switch (sortBy.value) {
      case 'clickCount':
        return b.clickCount - a.clickCount
      case 'title':
        return (a.title || '').localeCompare(b.title || '')
      case 'createdAt':
      default:
        return b.createdAt - a.createdAt
    }
  })
})

// Methods
const formatDate = (timestamp: number) => {
  return new Date(timestamp).toLocaleDateString()
}

const copyShortUrl = async (shortCode: string) => {
  try {
    await linksStore.copyToClipboard(linksStore.getFullShortUrl(shortCode))
    // You could add a toast notification here
  } catch (error) {
    console.error('Failed to copy URL:', error)
  }
}

const editLink = (link: Link) => {
  editingLink.value = link
}

const openLinkDetail = (link: Link) => {
  router.push(`/links/${link.shortCode}`)
}

const deleteLink = async (shortCode: string) => {
  if (confirm('Are you sure you want to delete this link?')) {
    try {
      await linksStore.deleteLink(shortCode)
    } catch (error) {
      console.error('Failed to delete link:', error)
    }
  }
}

const closeModal = () => {
  showCreateModal.value = false
  editingLink.value = null
}

const handleSaveLink = async (linkData: any) => {
  try {
    if (editingLink.value) {
      await linksStore.updateLink(editingLink.value.shortCode, linkData)
    } else {
      await linksStore.createLink(linkData)
    }
    closeModal()
  } catch (error) {
    console.error('Failed to save link:', error)
  }
}

// Load data on mount
onMounted(async () => {
  try {
    await Promise.all([
      linksStore.fetchLinks({ limit: 100 }),
      adminStore.fetchTenants()
    ])
  } catch (error) {
    console.error('Failed to load links:', error)
  }
})
</script>
